import { get, isObject, isString } from 'lodash';

import { applyPageGraphBaseRedirects } from '../../../constants/pageGraphBaseRedirects';
import routes from '../../../routes';
import rewardsRoutes from '../../../routes/rewards';
import siteRoutes from '../../../routes/site';
import orderingRoutes from '../../../routes/ordering';
import pagesRoutes from '../../../routes/pages';

/** Empty string = site lives at URL root. Use siteHomePath for Links / exact '/' matches. */
const sitePath = '';
const siteHomePath = '/';
const appPath = `/${rewardsRoutes.rewards}`;
const orderingPath = `/${orderingRoutes.ordering}`;
const pagesPath = `/${pagesRoutes.pages}`;
let siteOrderPath = `/${siteRoutes.containers.order}`;
const appOrderingPath = `${appPath}/${rewardsRoutes.containers.ordering}`;

/**
 * Platform `basePaths.web` (e.g. "" or "/site"). Prefixed onto joinSitePath and
 * the surface home root when the page graph is active.
 */
let siteWebBasePath = '';

/**
 * Where "go home" navigations land — surface root (`/` or `/site`), not a
 * page's own published path. Set by Site once the graph home pointer is built.
 */
let siteHomeHref = siteHomePath;

/** Normalize a platform base path to `''` or `/segment/...` (no trailing slash). */
const normalizeBasePath = (basePath = '') => {
  const normalized = String(basePath || '')
    .split('/')
    .filter(Boolean)
    .join('/');
  return normalized ? `/${normalized}` : '';
};

export const setSiteWebBasePath = (basePath = '') => {
  siteWebBasePath = normalizeBasePath(basePath);
  siteOrderPath = siteWebBasePath
    ? `${siteWebBasePath}/${siteRoutes.containers.order}`.replace(/\/{2,}/g, '/')
    : `/${siteRoutes.containers.order}`;
};

export const setSiteHomeHref = (href = siteHomePath) => {
  const path = String(href || siteHomePath).split('?')[0] || siteHomePath;
  siteHomeHref = path.startsWith('/') ? path : `/${path}`;
};

/** Contract: home CTAs and logo land on the surface base route. */
export const getSiteHomeHref = () => siteHomeHref || siteHomePath;

/**
 * Surface root for the web page graph: `/` when basePaths.web is empty,
 * `/site` when basePaths.web is `/site`. This is what home[surface] points at.
 */
export const getSurfaceRootPath = (basePath = siteWebBasePath) =>
  normalizeBasePath(basePath) || siteHomePath;

/** Join segments under the site base (root, or basePaths.web). Always absolute. */
export const joinSitePath = (...segments) => {
  const parts = [siteWebBasePath, ...segments].filter((s) => s !== '' && s != null);
  if (!parts.length) return getSurfaceRootPath();
  return `/${parts.join('/')}`.replace(/\/{2,}/g, '/');
};

/**
 * Coded path -> published path, registered by the Site shell once the page
 * graph has been read (see routing/siteGraph.js).
 *
 * It lives here, rather than in siteGraph, so the import stays one-way:
 * siteGraph needs joinSitePath, and every path helper below needs the resolver.
 * Unregistered (legacy mode, or the paint before routes are built) is identity,
 * so a brand with no graph behaves exactly as before.
 */
let sitePathResolver = null;

export const setSitePathResolver = (resolver) => {
  sitePathResolver = typeof resolver === 'function' ? resolver : null;
};

export const resolveSitePath = (pathname = '') =>
  (sitePathResolver ? sitePathResolver(pathname) : pathname);

/**
 * Platform `pageGraph.baseRedirects`. When set (non-null), they replace the
 * hardcoded /app and /ordering strips below. Null means legacy hardcoded aliases.
 */
let siteBaseRedirects = null;
/** `browser` | `webview` — matches SiteRedirectClient minus `any`. */
let siteRedirectClient = 'browser';

export const setSiteBaseRedirects = (redirects) => {
  siteBaseRedirects = Array.isArray(redirects) ? redirects : null;
};

export const setSiteRedirectClient = (client) => {
  siteRedirectClient = client === 'webview' ? 'webview' : 'browser';
};

const appScanPath = `${appPath}/${orderingRoutes.containers.scan}`;

/**
 * Apply pageGraph.baseRedirects as prefix rewrites before route matching.
 * Longest `from` wins. Carve-out: never rewrite /app/scan (QR deep links).
 * Status codes are for HTTP (prodServer); SPA soft-rewrites ignore them.
 */
export const applySiteBaseRedirects = (pathname = '', redirects = siteBaseRedirects) => {
  const { pathname: next } = applyPageGraphBaseRedirects(pathname, redirects, {
    client: siteRedirectClient,
    carveOuts: [appScanPath],
    homePath: siteHomePath,
  });
  return next;
};

/** Header account-menu panel keys that require login before opening. */
export const AUTH_REQUIRED_ACCOUNT_MENU_PANELS = new Set([
  'profile',
  'history',
  'myRewards',
]);

// Bare ordering sub-routes that some callers push without any leading surface,
// e.g. history.push('offers') from inside the menu page.
const relativeOrderSubRoutes = [
  orderingRoutes.containers.offers,
  orderingRoutes.containers.menuOffer,
  orderingRoutes.containers.menuItemOffer,
  orderingRoutes.containers.rewardList,
];

const startsWithSegment = (pathname, base) => {
  if (!base || base === '/') {
    return pathname === '/' || pathname.startsWith('/');
  }
  return pathname === base || pathname.startsWith(`${base}/`);
};

const stripQueryAndHash = (path = '') => {
  const withoutHash = String(path).split('#')[0];
  const [pathname, search = ''] = withoutHash.split('?');
  return { pathname, search: search ? `?${search}` : '' };
};

/**
 * Accepts either signature that react-router's history exposes:
 * push(path, state) and push({ pathname, search, state }).
 */
export const parseHistoryLocationArg = (path, state) => {
  if (isObject(path) && !isString(path)) {
    return {
      pathname: get(path, 'pathname', ''),
      search: get(path, 'search', '') || '',
      state: get(path, 'state', state),
    };
  }
  const { pathname, search } = stripQueryAndHash(path);
  return { pathname, search, state };
};

/**
 * The single normalizer for the Site surface. Any /app, /ordering, alias or
 * relative path becomes a root site location (/account, /order/...).
 *
 * App-root surfaces that live outside Site (e.g. /pages/privacy-policy) are
 * left unchanged so footer/chrome Links are not rewritten under Site.
 *
 * When `pageGraph.baseRedirects` are registered they run first (prefix rewrite
 * with client discriminator). Otherwise the hardcoded /app and /ordering
 * aliases below apply (legacy brands with no graph).
 *
 * Branch order for the legacy path is load-bearing: the /app/ordering and
 * /ordering rewrites must run before the generic /app rewrite.
 *
 * No /site → / backward strip (FUDR-14578).
 */
export const normalizeSiteLocation = (path, state) => {
  const parsed = parseHistoryLocationArg(path, state);
  let pathname = parsed.pathname || '';
  const wasRelative = pathname && !pathname.startsWith('/');

  if (!pathname.startsWith('/')) {
    pathname = `/${pathname}`;
  }

  const useGraphRedirects = Array.isArray(siteBaseRedirects) && siteBaseRedirects.length > 0;

  if (useGraphRedirects) {
    if (pathname !== '/') {
      pathname = applySiteBaseRedirects(pathname);
    }
  } else if (pathname === '/') {
    // Site home stays at /
  } else if (startsWithSegment(pathname, appOrderingPath)) {
    // /app/ordering/... -> /order/...
    pathname = `${siteOrderPath}${pathname.slice(appOrderingPath.length)}` || siteOrderPath;
  } else if (startsWithSegment(pathname, orderingPath)) {
    // /ordering/... -> /order/...
    // Site-scoped only: the standalone Ordering shell mounts /ordering at the
    // root Switch and never calls this normalizer.
    pathname = `${siteOrderPath}${pathname.slice(orderingPath.length)}` || siteOrderPath;
  } else if (startsWithSegment(pathname, appScanPath)) {
    // QR deep links stay under /app/scan
  } else if (startsWithSegment(pathname, appPath)) {
    // /app/... -> /... (covers /app/locations, /app/account, ...)
    const rest = pathname.slice(appPath.length);
    pathname = rest || siteHomePath;
  } else if (startsWithSegment(pathname, pagesPath)) {
    // /pages/... stays at app root (Privacy Policy, Terms, etc.)
  }

  if (wasRelative) {
    const isRelativeOrderSubRoute = relativeOrderSubRoutes.some((route) =>
      startsWithSegment(pathname, `/${route}`),
    );
    if (isRelativeOrderSubRoute && !startsWithSegment(pathname, siteOrderPath)) {
      pathname = `${siteOrderPath}${pathname}`;
    }
  }

  pathname = pathname.replace(/\/{2,}/g, '/') || siteHomePath;

  // Last step: a coded path becomes the path the graph published it at, so every
  // internal link keeps naming routes by their coded path. Paths the graph says
  // nothing about (including /pages/...) come back unchanged.
  pathname = resolveSitePath(pathname) || siteHomePath;

  return { pathname, search: parsed.search || '', state: parsed.state };
};

/** Path after site root. Returns '' for the site home itself. */
export const getSiteRouteKey = (pathname = '') => {
  if (pathname === siteHomePath || pathname === '' || pathname === sitePath) {
    return '';
  }
  return pathname.replace(/^\//, '');
};

/** Route keys match themselves and anything nested under them. */
export const matchesRouteKey = (pathKey, routeKey) => {
  if (!routeKey) return false;
  return pathKey === routeKey || pathKey.startsWith(`${routeKey}/`);
};

/** The account route owns the sidebar's own chrome, so it skips the shared header. */
export const isAccountSidebarPath = (pathname = '') =>
  startsWithSegment(pathname, resolveSitePath(joinSitePath(siteRoutes.containers.account)));

/** Where the header's account menu entries land inside the sidebar. */
export const getAccountMenuSidebarLocation = (panelKey) => {
  const at = (container, search = '') => ({
    pathname: joinSitePath(container),
    search,
    state: undefined,
  });
  switch (panelKey) {
    case 'history':
      return at(siteRoutes.containers.account, '?view=point-history');
    case 'myRewards':
      return at(siteRoutes.containers.myRewards);
    case 'rewardProgram':
      return at(siteRoutes.containers.rewardProgramDetails);
    case 'helpSupport':
      return at(siteRoutes.containers.helpSupport);
    case 'referral':
    case siteRoutes.containers.referral:
      return at(siteRoutes.containers.referral);
    case 'aboutUs':
    case siteRoutes.containers.aboutUs:
      return at(siteRoutes.containers.aboutUs);
    case 'vipPass':
    case siteRoutes.containers.vipMembershipList:
    case siteRoutes.containers.vipPassList:
      return at(siteRoutes.containers.vipMembershipList, '?view=account');
    case 'franchiseEnquiry':
    case siteRoutes.containers.franchiseEnquiry:
      return at(siteRoutes.containers.franchiseEnquiry);
    case 'workWithUs':
    case siteRoutes.containers.workWithUs:
      return at(siteRoutes.containers.workWithUs);
    case 'communityOrdering':
    case siteRoutes.containers.communityOrdering:
      return at(siteRoutes.containers.communityOrdering);
    case 'permissionsAndPrivacy':
    case siteRoutes.containers.permissionsAndPrivacy:
      return at(siteRoutes.containers.permissionsAndPrivacy);
    case 'profile':
      return at(siteRoutes.containers.account, '?view=profile');
    default: {
      // Static-page routes and other Site container keys (e.g. about-us).
      if (Object.values(siteRoutes.containers).includes(panelKey)) {
        return at(panelKey);
      }
      return at(siteRoutes.containers.account, '?view=profile');
    }
  }
};

/**
 * Main-page URL to show behind a location-detail popup once a direct link has
 * been hoisted into the popup.
 */
export const getLocationDetailPopupMainPath = (search = '', siteBasePath = siteHomePath) => {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const base = siteBasePath === '' ? siteHomePath : siteBasePath;
  if (params.get('view') === rewardsRoutes.containers.home) {
    return base;
  }
  if (base === siteHomePath) {
    return resolveSitePath(`/${siteRoutes.containers.locations}`);
  }
  return resolveSitePath(`${base}/${siteRoutes.containers.locations}`);
};

export { sitePath, siteHomePath, siteOrderPath };

/** True when pathname is the Site home (surface root from getSiteHomeHref). */
export const isSiteHomePathname = (pathname = '') => {
  const path = String(pathname || '').split('?')[0];
  const home = getSiteHomeHref();
  return path === home || path === `${home}/` || path === '' || path === sitePath;
};

/** True when pathname is under Site order (`/order` or nested). */
export const isSiteOrderPathname = (pathname = '') => {
  const path = String(pathname || '').split('?')[0];
  const orderBase = resolveSitePath(joinSitePath(siteRoutes.containers.order));
  return path === orderBase || path.startsWith(`${orderBase}/`);
};

/** True when pathname is Site menu (`/order/menu` or nested). */
export const isSiteMenuPathname = (pathname = '') => {
  const path = String(pathname || '').split('?')[0];
  const menuBase = resolveSitePath(
    joinSitePath(siteRoutes.containers.order, orderingRoutes.containers.menu),
  );
  return path === menuBase || path.startsWith(`${menuBase}/`);
};

/** Scan code segment from Site menu URL (`/order/menu/:scanCode`), or ''. */
export const getSiteMenuScanCodeFromPathname = (pathname = '') => {
  const path = String(pathname || '').split('?')[0];
  const menuBase = resolveSitePath(
    joinSitePath(siteRoutes.containers.order, orderingRoutes.containers.menu),
  );
  if (path !== menuBase && !path.startsWith(`${menuBase}/`)) {
    return '';
  }
  const rest = path.slice(menuBase.length).replace(/^\//, '');
  return rest.split('/')[0] || '';
};

const getScanCodeSegmentFromBase = (pathname = '', basePath = '') => {
  const path = String(pathname || '').split('?')[0];
  if (!basePath || (path !== basePath && !path.startsWith(`${basePath}/`))) {
    return '';
  }
  const rest = path.slice(basePath.length).replace(/^\//, '');
  return rest.split('/')[0] || '';
};

/**
 * Outlet-context deep link scanCode from URL: Site menu with code, or scan QR
 * (`/app/scan/:code` / legacy `/scan/:code`). Empty for bare `/order/menu` or `/app/scan`.
 */
export const getOutletContextScanCodeFromPathname = (pathname = '') => {
  const menuCode = getSiteMenuScanCodeFromPathname(pathname);
  if (menuCode) {
    return menuCode;
  }
  const appScanCode = getScanCodeSegmentFromBase(pathname, appScanPath);
  if (appScanCode) {
    return appScanCode;
  }
  return getScanCodeSegmentFromBase(pathname, `/${routes.containers.scan}`);
};

/** True when pathname is a menu-with-code or scan-QR deep link (skip nearest auto-select). */
export const isOutletContextDeepLink = (pathname = '') =>
  !!getOutletContextScanCodeFromPathname(pathname);
